/**
 * Discount code form shared by the cart page and the mini cart (snippets/discount-code-update.liquid).
 *
 * Applies and removes codes through the Cart Ajax API (`POST /cart/update.js` with a
 * `discount` field). The response carries `discount_codes[].applicable`, so an invalid
 * or non-applicable code is reported inline without leaving the page. A code that does
 * apply refreshes the cart so the Liquid-rendered totals and line prices update.
 *
 * Liquid lists the discounts that apply (cart.discount_applications). A code that sits on
 * the cart without applying, e.g. one that came from a CRM link while the cart only holds
 * excluded pieces, is read from /cart.js on load and added to the same list, with an
 * explanation, so the customer is told about it.
 *
 * A code coming from a "/discount/CODE" link is only stored by Shopify in the `discount_code`
 * cookie: it is applied at checkout but the cart does not know about it, so neither Liquid nor
 * /cart.js shows it. On load, when the cart carries no code but that cookie does, the code is
 * applied to the cart through the same Ajax call so the customer sees it right away.
 */
class CartDiscountForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="discount"]');
    this.submitButton = this.querySelector('[type="submit"]');
    this.errorElement = this.querySelector('[data-discount-error]');
    this.appliedList = this.querySelector('[data-discount-applied]');
    this.template = this.querySelector('[data-discount-template]');

    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.input.addEventListener('input', () => this.setError(''));
    this.appliedList.addEventListener('click', (event) => {
      if (event.target.closest('[data-discount-remove]')) {
        this.onRemove(event);
      }
    });

    if (this.appliedList.querySelector('[data-discount-item]')) {
      this.expand();
    }

    this.syncCartCodes();
  }

  // Codes on the cart that Liquid did not render (a non-applicable one, typically) are added
  // here so the customer sees them and can remove them. A code that only lives in the cookie
  // set by a "/discount/CODE" link is applied to the cart first.
  async syncCartCodes() {
    try {
      const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await response.json();
      const codes = cart.discount_codes || [];
      const cookieCode = this.getCookieCode();

      if (codes.length === 0 && cookieCode !== '' && cart.item_count > 0) {
        const updatedCart = await this.updateDiscount(cookieCode);
        const applied = (updatedCart.discount_codes || []).find((item) => item.applicable);

        if (applied) {
          this.refresh();
          return;
        }

        codes.push(...(updatedCart.discount_codes || []));
      }

      const rendered = Array.from(this.appliedList.querySelectorAll('[data-discount-item]')).map(
        (item) => (item.dataset.discountCodeValue || '').toLowerCase()
      );

      codes.forEach((item) => {
        if (!rendered.includes(item.code.toLowerCase())) {
          this.addCodeRow(item.code);
        }

        if (!item.applicable) {
          this.setError(this.dataset.textNotApplicable.replace('[code]', item.code));
        }
      });
    } catch (error) {
      // Nothing to show: the form still works without this information.
    }
  }

  getCookieCode() {
    const match = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);

    try {
      return match ? decodeURIComponent(match[1]).trim() : '';
    } catch (error) {
      return '';
    }
  }

  addCodeRow(code) {
    const row = this.template.content.firstElementChild.cloneNode(true);
    row.dataset.discountCodeValue = code;
    row.querySelector('[data-discount-code]').textContent = code;
    this.appliedList.appendChild(row);
    this.appliedList.classList.remove('hidden');
    this.expand();
  }

  // The form lives inside a collapsible (cart-summary): a cart with a discount opens it.
  expand() {
    const collapsible = this.closest('collapsible-content');
    const toggle = collapsible && document.querySelector(`[aria-controls="${collapsible.id}"]`);

    if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
      toggle.setAttribute('aria-expanded', 'true');
    }
  }

  async onSubmit(event) {
    event.preventDefault();

    const code = this.input.value.trim();

    if (code === '') {
      this.setError(this.dataset.textEmpty);
      this.input.focus();
      return;
    }

    this.setLoading(true);

    try {
      const cart = await this.updateDiscount(code);
      const discountCode = (cart.discount_codes || []).find(
        (item) => item.code.toLowerCase() === code.toLowerCase()
      );

      if (discountCode && discountCode.applicable) {
        this.refresh();
        return;
      }

      // Shopify keeps a non-applicable code on the cart. Drop it so the customer is not
      // greeted by the same error on every later visit.
      await this.updateDiscount('');
      this.setError(this.dataset.textNotApplicable.replace('[code]', code));
    } catch (error) {
      this.setError(this.dataset.textError);
    }

    this.setLoading(false);
  }

  async onRemove(event) {
    event.preventDefault();

    const button = event.target.closest('[data-discount-remove]');
    button.disabled = true;

    try {
      await this.updateDiscount('');
      this.refresh();
    } catch (error) {
      button.disabled = false;
      this.setError(this.dataset.textError);
    }
  }

  // Inside the mini cart drawer the theme re-renders the drawer on "cart:refresh"; the cart page
  // still reloads so its Liquid totals and line prices update.
  refresh() {
    if (this.closest('#mini-cart')) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    window.location.reload();
  }

  async updateDiscount(code) {
    const response = await fetch(`${window.themeVariables.routes.cartUrl}/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    });

    if (!response.ok) {
      throw new Error(`Cart update failed with status ${response.status}`);
    }

    return response.json();
  }

  setError(message) {
    this.errorElement.textContent = message;
  }

  setLoading(isLoading) {
    this.submitButton.disabled = isLoading;
    this.input.disabled = isLoading;
    this.classList.toggle('cart-discount--loading', isLoading);
  }
}

window.customElements.define('cart-discount-form', CartDiscountForm);
